import React from "react";
import { CheckCircle2, HeartHandshake, CalendarDays, Cake, ChevronRight, Mic2, Droplets, Heart } from "lucide-react";
import { formatDateRange } from "@/lib/date-utils";
import { formatYearsLabel } from "@/lib/date";

function buildHumanContext(visits, events, importantCount, preachings) {
  const parts = [];
  if (preachings.length > 0) parts.push(preachings.length === 1 ? "una predicación" : `${preachings.length} predicaciones`);
  if (visits.length === 1) parts.push("una visita pastoral");
  else if (visits.length > 1) parts.push(`${visits.length} visitas pastorales`);
  if (events.length === 1) parts.push("un evento");
  else if (events.length > 1) parts.push(`${events.length} eventos`);

  if (parts.length === 0 && importantCount > 0) {
    return importantCount === 1
      ? "Hay una fecha importante que atender hoy."
      : `Hay ${importantCount} fechas importantes hoy.`;
  }
  if (parts.length === 0) return null;

  const joined = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} y ${parts.at(-1)}` : parts[0];
  return `Te espera ${joined} hoy.`;
}

function TodayPriority({
  urgentCount,
  todayPlan,
  todayVisits,
  todayEvents,
  todayImportantCount,
  birthdaysToday,
  baptismsToday = [],
  anniversariesToday = [],
  anniversariesUpcoming = [],
  todayPreachings = [],
  navigate,
}) {
  const hasPlan     = !!todayPlan;
  const hasActivity = urgentCount > 0;

  const humanContext = buildHumanContext(todayVisits, todayEvents, todayImportantCount, todayPreachings);

  // Plan → always a contextual pill, never the headline.
  // The headline is always the pastoral reading of the day.
  const isCalmDay   = !hasActivity;
  const headerLabel = hasActivity ? "pastoral · hoy" : "hoy";
  const headerTitle = hasActivity
    ? "Tienes actividad pastoral hoy"
    : "La obra avanza con calma";
  const headerSub   = hasActivity
    ? (humanContext || "Un día de obra pastoral por delante.")
    : "Un día para sembrar en silencio.";

  const _todayMM = String(new Date().getMonth() + 1).padStart(2, '0');
  const _todayDD = String(new Date().getDate()).padStart(2, '0');
  const sourceAnniversaries = anniversariesToday.length > 0
    ? anniversariesToday
    : anniversariesUpcoming.filter(p => {
        const [, m, d] = (p.marriage_date ?? '').split('-');
        return m === _todayMM && d === _todayDD;
      });

  const hasItems =
    todayVisits.length > 0 || todayEvents.length > 0 || birthdaysToday.length > 0 ||
    baptismsToday.length > 0 || todayPreachings.length > 0 || sourceAnniversaries.length > 0;

  // Group by family_id + marriage_date so couples appear as one item
  const groupedAnniversariesToday = (() => {
    const coupleMap = {};
    const solo = [];
    for (const p of sourceAnniversaries) {
      const dateKey = p.marriage_date?.slice(0, 10);
      if (p.family_id && dateKey) {
        const key = `${p.family_id}_${dateKey}`;
        if (!coupleMap[key]) coupleMap[key] = [];
        coupleMap[key].push(p);
      } else {
        solo.push({ type: "solo", person: p });
      }
    }
    const result = [];
    for (const members of Object.values(coupleMap)) {
      if (members.length >= 2) result.push({ type: "couple", members });
      else result.push({ type: "solo", person: members[0] });
    }
    return [...result, ...solo].slice(0, 3);
  })();

  return (
    <div className="px-4">
      <div className="rounded-[20px] overflow-hidden bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700/60 shadow-card">

        {/* ── CABECERA ─────────────────────────────────────── */}
        <div className="flex items-start gap-3.5 px-4 pt-4 pb-3.5">
          <div className={`h-10 w-10 rounded-[12px] flex items-center justify-center shrink-0 mt-0.5 ${
            isCalmDay
              ? "bg-emerald-50 dark:bg-emerald-900/20"
              : "bg-slate-50 dark:bg-slate-700/80"
          }`}>
            {isCalmDay
              ? <CheckCircle2 size={18} className="text-emerald-500" />
              : <CalendarDays size={18} className="text-slate-500 dark:text-slate-400" />
            }
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-widest text-slate-400 dark:text-slate-500">
              {headerLabel}
            </p>
            {hasPlan && (
              <p className="mt-0.5 text-[15px] font-medium text-slate-600 dark:text-slate-300 leading-snug">
                {todayPlan}
              </p>
            )}
            <h2 className={`text-[17px] font-semibold text-slate-800 dark:text-slate-100 leading-snug ${hasPlan ? "mt-0" : "mt-0.5"}`}>
              {headerTitle}
            </h2>
            <p className="mt-1.5 text-[12px] leading-snug text-slate-500 dark:text-slate-400">
              {headerSub}
            </p>
          </div>
        </div>

        {/* ── FILAS DE ITEMS ───────────────────────────────── */}
        {hasItems && (
          <div className="border-t border-slate-100 dark:border-slate-700/40">

            {/* Visitas de hoy */}
            {todayVisits.slice(0, 3).map((v) => (
              <button
                key={`tv-${v.id}`}
                onClick={() => navigate(`/visits/${v.id}`)}
                className="
                  w-full flex items-center gap-3 px-4 py-3
                  border-b border-slate-100/80 dark:border-slate-700/30 last:border-0
                  text-left transition-colors duration-150
                  active:bg-slate-50 dark:active:bg-slate-700/30
                "
              >
                <div className="h-9 w-9 rounded-[11px] bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
                  <HeartHandshake size={15} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate leading-snug">{v.contact_name}</p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">Visita programada para hoy</p>
                </div>
                <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
              </button>
            ))}

            {/* Eventos de hoy */}
            {todayEvents.slice(0, 3).map((e) => {
              const isPersonal = e._source === "Personal";
              const iconBg = isPersonal
                ? "bg-violet-50 dark:bg-violet-900/20"
                : e.church_id
                ? "bg-blue-50 dark:bg-blue-900/20"
                : "bg-amber-50 dark:bg-amber-900/15";
              const iconColor = isPersonal
                ? "text-violet-600"
                : e.church_id
                ? "text-blue-600"
                : "text-amber-600";
              const dateStr = formatDateRange(e.date || e.start_date, e.end_date);
              return (
                <button
                  key={`te-${e.id}`}
                  onClick={() => navigate("/agenda")}
                  className="
                    w-full flex items-center gap-3 px-4 py-3
                    border-b border-slate-100/80 dark:border-slate-700/30 last:border-0
                    text-left transition-colors duration-150
                    active:bg-slate-50 dark:active:bg-slate-700/30
                  "
                >
                  <div className={`h-9 w-9 rounded-[11px] flex items-center justify-center shrink-0 ${iconBg}`}>
                    <CalendarDays size={15} className={iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate leading-snug">{e.title}</p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                      {e._source ? `${e._source} · ${dateStr}` : dateStr}
                    </p>
                  </div>
                  <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
                </button>
              );
            })}

            {/* Aniversarios de bautismo hoy */}
            {baptismsToday.slice(0, 3).map((p) => {
              const yearsLabel = formatYearsLabel(p.baptism_date, "baptism");
              return (
                <button
                  key={`tbap-${p.id}`}
                  onClick={() => navigate(`/people/${p.id}`)}
                  className="
                    w-full flex items-center gap-3 px-4 py-3
                    border-b border-slate-100/80 dark:border-slate-700/30 last:border-0
                    text-left transition-colors duration-150
                    active:bg-slate-50 dark:active:bg-slate-700/30
                  "
                >
                  <div className="h-9 w-9 rounded-[11px] bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center shrink-0">
                    <Droplets size={15} className="text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate leading-snug">{p.name}</p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                      {yearsLabel || "Aniversario de bautismo"}
                    </p>
                  </div>
                  <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
                </button>
              );
            })}

            {/* Predicación de hoy */}
            {todayPreachings.slice(0, 2).map((a) => (
              <button
                key={`tp-${a.id}`}
                onClick={() => navigate("/preaching")}
                className="
                  w-full flex items-center gap-3 px-4 py-3
                  border-b border-slate-100/80 dark:border-slate-700/30 last:border-0
                  text-left transition-colors duration-150
                  active:bg-slate-50 dark:active:bg-slate-700/30
                "
              >
                <div className="h-9 w-9 rounded-[11px] bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center shrink-0">
                  <Mic2 size={15} className="text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate leading-snug">
                    {a.church_name || "Predicación"}
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                    {["Predicación", a.context].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
              </button>
            ))}

            {/* Aniversarios de matrimonio hoy */}
            {groupedAnniversariesToday.map((entry, idx) => {
              if (entry.type === "couple") {
                const [p1, p2] = entry.members;
                const nombre2   = p2.name.split(" ")[0];
                const conector  = nombre2.match(/^[IiÍí]/) ? "e" : "y";
                const title     = `${p1.name.split(" ")[0]} ${conector} ${nombre2}`;
                const yearsLabel = formatYearsLabel(p1.marriage_date, "marriage");
                return (
                  <button
                    key={`tann-couple-${p1.family_id}-${idx}`}
                    onClick={() => navigate(`/families/${p1.family_id}`)}
                    className="
                      w-full flex items-center gap-3 px-4 py-3
                      border-b border-slate-100/80 dark:border-slate-700/30 last:border-0
                      text-left transition-colors duration-150
                      active:bg-slate-50 dark:active:bg-slate-700/30
                    "
                  >
                    <div className="h-9 w-9 rounded-[11px] bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center shrink-0">
                      <Heart size={15} className="text-rose-500 dark:text-rose-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate leading-snug">{title}</p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                        {yearsLabel || "Aniversario de matrimonio"}
                      </p>
                    </div>
                    <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
                  </button>
                );
              }
              const p = entry.person;
              const yearsLabel = formatYearsLabel(p.marriage_date, "marriage");
              return (
                <button
                  key={`tann-${p.id}`}
                  onClick={() => navigate(p.family_id ? `/families/${p.family_id}` : `/people/${p.id}`)}
                  className="
                    w-full flex items-center gap-3 px-4 py-3
                    border-b border-slate-100/80 dark:border-slate-700/30 last:border-0
                    text-left transition-colors duration-150
                    active:bg-slate-50 dark:active:bg-slate-700/30
                  "
                >
                  <div className="h-9 w-9 rounded-[11px] bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center shrink-0">
                    <Heart size={15} className="text-rose-500 dark:text-rose-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate leading-snug">{p.name}</p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                      {yearsLabel || "Aniversario de matrimonio"}
                    </p>
                  </div>
                  <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
                </button>
              );
            })}

            {/* Cumpleaños de hoy */}
            {birthdaysToday.slice(0, 3).map((p) => (
              <button
                key={`tb-${p.id}`}
                onClick={() => navigate(`/people/${p.id}`)}
                className="
                  w-full flex items-center gap-3 px-4 py-3
                  border-b border-slate-100/80 dark:border-slate-700/30 last:border-0
                  text-left transition-colors duration-150
                  active:bg-slate-50 dark:active:bg-slate-700/30
                "
              >
                <div className="h-9 w-9 rounded-[11px] bg-pink-50 dark:bg-pink-900/20 flex items-center justify-center shrink-0">
                  <Cake size={15} className="text-pink-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate leading-snug">{p.name}</p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{(() => {
                    const birthYear = new Date(p.birth_date ?? p.birthdate ?? 0).getFullYear();
                    const age = new Date().getFullYear() - birthYear;
                    return age > 0 ? `Cumple ${age} años` : "Cumpleaños hoy";
                  })()}</p>
                </div>
                <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
              </button>
            ))}

          </div>
        )}

      </div>
    </div>
  );
}

export default React.memo(TodayPriority);
